/**
 * 商品カタログ (product_catalogs) の hooks。Phase J 重層化対応。
 *
 * - 一覧読込: orderBy('createdAt', 'desc')
 * - 有効カタログ: where('isActive', '==', true) — category 内で 1 件のみ
 * - items サブコレクション読込
 * - upsert / activate / delete / bulkImport は Callable
 */
import type {
  CatalogItem,
  ProductCatalog,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface ProductCatalogRecord extends ProductCatalog {
  id: string;
}

export interface CatalogItemRecord extends CatalogItem {
  id: string;
}

// ─── Queries ───

/** 全カタログ (category, version の両軸表示用) */
export function useProductCatalogs() {
  return useQuery({
    queryKey: ['product_catalogs', 'all'],
    queryFn: async (): Promise<ProductCatalogRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'product_catalogs'), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as ProductCatalog),
      }));
    },
  });
}

/** 単一カタログ */
export function useProductCatalog(catalogId: string | null | undefined) {
  return useQuery({
    queryKey: ['product_catalogs', 'detail', catalogId],
    enabled: !!catalogId,
    queryFn: async (): Promise<ProductCatalogRecord | null> => {
      if (!catalogId) return null;
      const snap = await getDoc(doc(db, 'product_catalogs', catalogId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as ProductCatalog) };
    },
  });
}

/** カタログの items 一覧 (position ASC) */
export function useCatalogItems(catalogId: string | null | undefined) {
  return useQuery({
    queryKey: ['product_catalogs', catalogId, 'items'],
    enabled: !!catalogId,
    queryFn: async (): Promise<CatalogItemRecord[]> => {
      if (!catalogId) return [];
      const snap = await getDocs(
        query(
          collection(db, 'product_catalogs', catalogId, 'items'),
          orderBy('position', 'asc'),
        ),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as CatalogItem),
      }));
    },
  });
}

/**
 * category 別に有効カタログを取得 (見積/個別説明記録の商品ピッカーで使用)。
 * v3.2.23+: settings.seasonMode に対応する season のカタログのみを返す。
 */
export function useActiveCatalogs() {
  return useQuery({
    queryKey: ['product_catalogs', 'active', 'season_aware'],
    queryFn: async (): Promise<ProductCatalogRecord[]> => {
      const snap = await getDocs(
        query(
          collection(db, 'product_catalogs'),
          where('isActive', '==', true),
        ),
      );
      // 現在の seasonMode を取得 (未設定は 'normal')
      const settingsSnap = await getDoc(doc(db, 'settings', 'sales'));
      const seasonMode: 'normal' | 'sale' =
        ((settingsSnap.exists() ? settingsSnap.data() : null)
          ?.seasonMode === 'sale')
          ? 'sale'
          : 'normal';
      return snap.docs
        .map((d) => ({
          id: d.id,
          ...(d.data() as ProductCatalog),
        }))
        .filter((c) => (c.season ?? 'normal') === seasonMode);
    },
  });
}

/**
 * 全有効カタログの items を一括取得 (個別説明記録など複数カテゴリ横断 UI 用)。
 * 各 item には catalogId / catalogCategory が付加される。
 * v3.2.23+: settings.seasonMode に対応する season のカタログのみ。
 */
export interface MergedCatalogItem extends CatalogItemRecord {
  catalogId: string;
  catalogCategory: string;
  catalogVersion: string;
}

export function useAllActiveCatalogItems() {
  return useQuery({
    queryKey: ['product_catalogs', 'active', 'items_all', 'season_aware'],
    queryFn: async (): Promise<MergedCatalogItem[]> => {
      const snap = await getDocs(
        query(
          collection(db, 'product_catalogs'),
          where('isActive', '==', true),
        ),
      );
      // 現在の seasonMode を取得 (未設定は 'normal')
      const settingsSnap = await getDoc(doc(db, 'settings', 'sales'));
      const seasonMode: 'normal' | 'sale' =
        ((settingsSnap.exists() ? settingsSnap.data() : null)
          ?.seasonMode === 'sale')
          ? 'sale'
          : 'normal';
      // season に一致するカタログだけを残す
      const effectiveDocs = snap.docs.filter((d) => {
        const cat = d.data() as ProductCatalog;
        return (cat.season ?? 'normal') === seasonMode;
      });
      const out: MergedCatalogItem[] = [];
      for (const d of effectiveDocs) {
        const cat = d.data() as ProductCatalog;
        const itemsSnap = await getDocs(
          query(
            collection(db, 'product_catalogs', d.id, 'items'),
            orderBy('position', 'asc'),
          ),
        );
        for (const i of itemsSnap.docs) {
          out.push({
            id: i.id,
            ...(i.data() as CatalogItem),
            catalogId: d.id,
            catalogCategory: cat.category,
            catalogVersion: cat.version,
          });
        }
      }
      return out;
    },
  });
}

/**
 * 指定の報酬セット (commissionSet) に紐付くカタログの items を一括取得。
 *
 * v3.2.30+ バグ修正: 報酬セット編集系の画面は「現在の季節モード」ではなく、
 * そのセット自身に紐付いた (`linkedCommissionSetId === setId`) カタログの価格を
 * 参照すべき。`useAllActiveCatalogItems()` は global な settings.seasonMode で
 * カタログを絞るため、例えば「通常期」セットを開いている最中に季節モードが
 * 「セール期」だと、セール価格を税抜ベースに報酬を誤算出していた。
 *
 * この hook は seasonMode に依存せず、setId に紐付いた有効カタログのみを返す。
 */
export function useCatalogItemsForCommissionSet(
  setId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['product_catalogs', 'by_commission_set', 'items', setId],
    enabled: !!setId,
    queryFn: async (): Promise<MergedCatalogItem[]> => {
      if (!setId) return [];
      const snap = await getDocs(
        query(
          collection(db, 'product_catalogs'),
          where('isActive', '==', true),
        ),
      );
      // このセットに紐付いた (linkedCommissionSetId 一致) カタログだけを残す
      const linkedDocs = snap.docs.filter((d) => {
        const cat = d.data() as ProductCatalog;
        return (cat.linkedCommissionSetId ?? null) === setId;
      });
      const out: MergedCatalogItem[] = [];
      for (const d of linkedDocs) {
        const cat = d.data() as ProductCatalog;
        const itemsSnap = await getDocs(
          query(
            collection(db, 'product_catalogs', d.id, 'items'),
            orderBy('position', 'asc'),
          ),
        );
        for (const i of itemsSnap.docs) {
          out.push({
            id: i.id,
            ...(i.data() as CatalogItem),
            catalogId: d.id,
            catalogCategory: cat.category,
            catalogVersion: cat.version,
          });
        }
      }
      return out;
    },
  });
}

// ─── Mutations ───

const upsertFn = httpsCallable<
  {
    id?: string;
    category: string;
    version: string;
    label?: string;
    isActive?: boolean;
    notes?: string | null;
    /** v3.2.23+: 季節タグ。'normal' or 'sale'。 */
    season?: 'normal' | 'sale';
    /** v3.2.23+: 紐付け CommissionSet ID。null で解除。 */
    linkedCommissionSetId?: string | null;
  },
  { id: string }
>(functions, 'upsertProductCatalog');

export function useUpsertProductCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof upsertFn>[0]) => {
      const r = await upsertFn(input);
      return r.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['product_catalogs'] }),
  });
}

const activateFn = httpsCallable<
  { id: string },
  { id: string; category: string }
>(functions, 'activateProductCatalog');

export function useActivateProductCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await activateFn({ id });
      return r.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['product_catalogs'] }),
  });
}

const deleteFn = httpsCallable<
  { id: string; force?: boolean },
  { id: string; itemsDeleted: number }
>(functions, 'deleteProductCatalog');

export function useDeleteProductCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; force?: boolean }) => {
      const r = await deleteFn(input);
      return r.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['product_catalogs'] }),
  });
}

interface BulkImportInput {
  catalogId: string;
  /**
   * 'replace' : 既存 items を全削除して入れ替え。ドキュメント ID が全部変わるため
   *             **新規カタログ作成時のみ**。既存カタログに使うと報酬セットの
   *             productId 参照が総 orphan 化する。
   * 'sync'    : SKU 突合で上書き。既存 ID を維持し、シートに無い SKU は削除。
   *             既存カタログを最新価格表で上書きする用途はこれ。
   * 'append'  : 追加のみ (重複 sku は上書き、既存は残す)
   */
  mode?: 'replace' | 'sync' | 'append';
  items: Array<{
    name: string;
    modelName: string;
    variation?: string | null;
    sku: string;
    /** 価格表の「製品カテゴリ」列 (Module / Hardware ...)。商談の商品カテゴリーとは別物。 */
    productCategory?: string | null;
    description?: string | null;
    costPrice: number;
    priceExcludingTax: number;
    priceIncludingTax: number;
    supplierName?: string | null;
  }>;
}

interface BulkImportResult {
  catalogId: string;
  total: number;
  created: number;
  updated: number;
  deletedBeforeImport: number;
  /** mode='sync' でシートに存在せず削除した件数 */
  deletedMissing: number;
  /** mode='sync' で ID を維持して更新できた件数 */
  preservedIds: number;
}

const bulkImportFn = httpsCallable<BulkImportInput, BulkImportResult>(
  functions,
  'bulkImportToCatalog',
);

export function useBulkImportToCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkImportInput) => {
      const r = await bulkImportFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['product_catalogs'] });
      qc.invalidateQueries({
        queryKey: ['product_catalogs', vars.catalogId, 'items'],
      });
    },
  });
}

// ─── 単品の手動 upsert / delete (Phase J 拡張) ───

interface UpsertItemInput {
  catalogId: string;
  /** 指定時は更新、未指定 (or 空) は新規 */
  itemId?: string;
  name: string;
  modelName: string;
  variation?: string | null;
  sku: string;
  /** 価格表の「製品カテゴリ」列。商談の商品カテゴリーとは別物。 */
  productCategory?: string | null;
  description?: string | null;
  costPrice: number;
  priceExcludingTax: number;
  priceIncludingTax: number;
  supplierName?: string | null;
  /** true = メーカー発注メールの商品欄に含めない。 */
  excludeFromManufacturerOrder?: boolean;
}

interface UpsertItemResult {
  catalogId: string;
  itemId: string;
  created: boolean;
}

const upsertCatalogItemFn = httpsCallable<UpsertItemInput, UpsertItemResult>(
  functions,
  'upsertCatalogItem',
);

export function useUpsertCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertItemInput) => {
      const r = await upsertCatalogItemFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['product_catalogs'] });
      qc.invalidateQueries({
        queryKey: ['product_catalogs', vars.catalogId, 'items'],
      });
    },
  });
}

const deleteCatalogItemFn = httpsCallable<
  { catalogId: string; itemId: string },
  { ok: true }
>(functions, 'deleteCatalogItem');

export function useDeleteCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { catalogId: string; itemId: string }) => {
      const r = await deleteCatalogItemFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['product_catalogs'] });
      qc.invalidateQueries({
        queryKey: ['product_catalogs', vars.catalogId, 'items'],
      });
    },
  });
}
